import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { CheckCircle, XCircle, Loader2, Calendar, MapPin, FileText } from 'lucide-react';
import axios from 'axios';
import ScanTicketZ, { champsLus } from "../components/ScanTicketZ";

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function BilanPublicPage() {
  const { token } = useParams();
  
  const [status, setStatus] = useState('loading'); // loading, form, submitting, success, error, already_submitted
  const [bilanInfo, setBilanInfo] = useState(null);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    nombre_plats: '',
    ca_ht: '',
    ca_ttc: '',
    commentaire: ''
  });

  useEffect(() => {
    const loadBilanInfo = async () => {
      try {
        const response = await axios.get(`${API}/api/bilans/manual/${token}`);
        setBilanInfo(response.data);
        
        if (response.data.already_submitted) {
          setStatus('already_submitted');
        } else {
          setStatus('form');
        }
      } catch (err) {
        console.error('Error loading bilan:', err);
        setError(err.response?.data?.detail || 'Lien invalide ou expiré');
        setStatus('error');
      }
    };

    if (token) {
      loadBilanInfo();
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    
    try {
      await axios.post(`${API}/api/bilans/manual/${token}`, {
        nombre_plats: formData.nombre_plats ? parseInt(formData.nombre_plats) : null,
        ca_ht: formData.ca_ht ? parseFloat(formData.ca_ht) : null,
        ca_ttc: formData.ca_ttc ? parseFloat(formData.ca_ttc) : null,
        commentaire: formData.commentaire || null
      });
      
      setStatus('success');
    } catch (err) {
      console.error('Error submitting bilan:', err);
      setError(err.response?.data?.detail || 'Erreur lors de l\'envoi');
      setStatus('error');
    }
  };

  if (status === 'loading' || status === 'submitting') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 text-center">
            <Loader2 className="w-12 h-12 animate-spin text-amber-500 mx-auto mb-4" />
            <p className="text-gray-600">
              {status === 'loading' ? 'Chargement...' : 'Envoi en cours...'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-red-50 to-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-red-200">
          <CardHeader className="text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-2" />
            <CardTitle className="text-red-700">Erreur</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-gray-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'success' || status === 'already_submitted') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-green-200">
          <CardHeader className="text-center bg-green-50 rounded-t-lg">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-2" />
            <CardTitle className="text-green-700">
              {status === 'already_submitted' ? 'Bilan déjà soumis' : 'Merci !'}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 text-center">
            <p className="text-gray-600 mb-4">
              {status === 'already_submitted' 
                ? 'Vous avez déjà rempli ce bilan de prestation.'
                : 'Votre bilan de prestation a bien été enregistré.'}
            </p>
            {bilanInfo && (
              <div className="bg-amber-50 rounded-lg p-4 border border-amber-200 text-left">
                <p className="font-semibold text-amber-800">{bilanInfo.event_title}</p>
                <p className="text-sm text-gray-600 mt-1">{bilanInfo.event_date} - {bilanInfo.event_lieu}</p>
              </div>
            )}
            <Button 
              className="mt-6 bg-amber-500 hover:bg-amber-600"
              onClick={() => window.close()}
            >
              Fermer cette page
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Form state
  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white flex items-center justify-center p-4">
      <Card className="w-full max-w-lg border-amber-200 shadow-lg">
        <CardHeader className="text-center bg-amber-50 rounded-t-lg">
          <FileText className="w-12 h-12 text-amber-600 mx-auto mb-2" />
          <CardTitle className="text-amber-800 text-xl">
            Bilan de prestation
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          {bilanInfo && (
            <div className="bg-white rounded-lg p-4 border border-amber-200 mb-6">
              <h3 className="font-semibold text-amber-800 text-lg mb-2">
                {bilanInfo.event_title}
              </h3>
              <div className="space-y-1 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-amber-600" />
                  <span>{bilanInfo.event_date}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-amber-600" />
                  <span>{bilanInfo.event_lieu}</span>
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Food Truck : <strong>{bilanInfo.food_truck_nom}</strong>
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <ScanTicketZ chemin={`/ticket-z/lire/${token}`}
              onLu={(r) => setFormData((f) => ({ ...f, ...champsLus(r) }))} />
            <div className="space-y-2">
              <Label htmlFor="nombre_plats">Nombre de plats vendus</Label>
              <Input
                id="nombre_plats"
                type="number"
                min="0"
                placeholder="Ex: 150"
                value={formData.nombre_plats}
                onChange={(e) => setFormData({...formData, nombre_plats: e.target.value})}
                data-testid="nombre-plats-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ca_ht">Chiffre d'affaires HT (€)</Label>
                <Input
                  id="ca_ht"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Ex: 1250.00"
                  value={formData.ca_ht}
                  onChange={(e) => setFormData({...formData, ca_ht: e.target.value})}
                  data-testid="ca-ht-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ca_ttc">Chiffre d'affaires TTC (€)</Label>
                <Input
                  id="ca_ttc"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Ex: 1500.00"
                  value={formData.ca_ttc}
                  onChange={(e) => setFormData({...formData, ca_ttc: e.target.value})}
                  data-testid="ca-ttc-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="commentaire">Commentaires (optionnel)</Label>
              <Textarea
                id="commentaire"
                placeholder="Retour sur l'événement, suggestions..."
                rows={3}
                value={formData.commentaire}
                onChange={(e) => setFormData({...formData, commentaire: e.target.value})}
                data-testid="commentaire-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-lg"
              data-testid="submit-bilan-btn"
            >
              Envoyer mon bilan
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
